import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { authorizedAxios, useAuth } from "@moonscape/shared";

type UseUkSa108Options = {
  taxYear: number;
  enabled?: boolean;
};

export type UkSa108Totals = {
  disposals: number;
  proceedsGBP: number;
  allowableCostsGBP: number;
  gainsGBP: number;
  lossesGBP: number;
  taxableGainGBP: number;
  allowanceUsedGBP: number;
};

export type UkSa108Response = {
  taxYear: number;
  taxSetting: any;
  totals: UkSa108Totals;
  taxEvents: any[];
};

export const useUkSa108 = ({
  taxYear,
  enabled = true,
}: UseUkSa108Options): UseQueryResult<UkSa108Response | null, Error> => {
  const { getToken, user } = useAuth();

  const fetchSa108 = async (): Promise<UkSa108Response | null> => {
    const token = await getToken();
    try {
      const response = await authorizedAxios(token).get(
        "/export/tax-report/uk/sa108",
        {
          params: { year: taxYear },
          validateStatus: (status) =>
            (status >= 200 && status < 300) || status === 204,
        }
      );

      if (response.status === 204) {
        return null;
      }

      return response.data;
    } catch (error: any) {
      const status = error?.response?.status;
      const code = error?.response?.data?.error;
      if (status === 400 && code === "unsupported_jurisdiction") {
        return null;
      }
      throw error;
    }
  };

  return useQuery({
    queryKey: ["uk_sa108", taxYear],
    queryFn: fetchSa108,
    enabled: enabled && !!user?.email && Number.isFinite(taxYear),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });
};
